/**
 * What ⇪ opens on something a group has published: publish the draft again,
 * the version history with Restore, and withdraw.
 *
 * `docs/plans/group-accounts.md` §5, and the mock approved 2026-09-29. It
 * replaced a ⇪ that withdrew in one click, which left no way to push an update
 * to members at all. One component for layouts, screens and chart templates,
 * because the three differ only in what "publish" sends — the caller passes
 * that in.
 *
 * **"No changes since v3"** is the server's answer, not a guess here: a publish
 * whose content matches the current version records nothing and hands the same
 * number back.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { fetchVersions, restoreVersion, type VersionEntry, type VersionedKind } from "../api/versions";

export interface PublishedMenuProps {
  kind: VersionedKind;
  presetKey: string;
  /** The group it is published to, as the author reads it — `JP`. */
  groupName: string;
  /** The current published version, when the listing carried one. */
  current?: number;
  /**
   * Publish the draft. Resolves to the version the server recorded. Absent
   * where publishing is done by a form of its own — the screen editor's
   * Publish tab, the Save dialog — and this shows the history only.
   */
  onPublish?: () => Promise<number | undefined>;
  /** In the flow of a panel rather than floating under a button. */
  inline?: boolean;
  /** Told after a Restore so the caller can re-read its listing. */
  onRestored: () => void | Promise<void>;
  onWithdraw?: () => Promise<void>;
  onClose: () => void;
}

export function PublishedMenu({
  kind,
  presetKey,
  groupName,
  current,
  onPublish,
  onRestored,
  onWithdraw,
  onClose,
  inline = false,
}: PublishedMenuProps) {
  const [history, setHistory] = useState<VersionEntry[] | null>(null);
  const [latest, setLatest] = useState<number | undefined>(current);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * **Fixed, measured off the trigger — never `absolute`.** Its one floating
   * host is the layout panel, which scrolls (`overflowY: auto`) and is 400px
   * wide; right-aligned to a ⇪ in the middle of a row, an absolute menu hung
   * off the panel's left edge and was cut there (2026-10-04). The rule
   * LayoutPanel's own header states, now four times bitten. The anchor is the
   * element the menu is rendered inside, so no caller passes a ref; the menu
   * is clamped into the window and flips above the trigger when there is no
   * room below.
   */
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (inline) return;
    const menu = menuRef.current;
    const anchor = menu?.parentElement;
    if (!menu || !anchor) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const w = menu.offsetWidth;
      const h = menu.offsetHeight;
      const left = Math.max(EDGE, Math.min(a.right - w, window.innerWidth - w - EDGE));
      const below = a.bottom + 2;
      const top =
        below + h <= window.innerHeight - EDGE ? below : Math.max(EDGE, a.top - h - 2);
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    // Capture: the panel's own scroll moves the trigger without the window's.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
    // The height moves as the history loads and as a note appears.
  }, [inline, history, message, error]);

  const load = async () => {
    try {
      const found = await fetchVersions(kind, presetKey);
      setHistory(found);
      if (found[0]) setLatest(found[0].version);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setHistory([]);
    }
  };

  useEffect(() => {
    void load();
    // `load` closes over the key it was made for; re-running on it is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, presetKey]);

  const run = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await work();
    } catch (e) {
      // Shown, never swallowed: a publish that failed quietly is one the author
      // tells the group about anyway.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const publish = () =>
    run(async () => {
      if (!onPublish) return;
      const before = latest;
      const after = await onPublish();
      setMessage(
        after !== undefined && after === before
          ? `No changes since v${after}`
          : after !== undefined
            ? `Published v${after} to ${groupName}`
            : `Published to ${groupName}`,
      );
      await load();
    });

  const restore = (version: number) =>
    run(async () => {
      const became = await restoreVersion(kind, presetKey, version);
      setMessage(became ? `v${version} is live again as v${became}` : `Restored v${version}`);
      await onRestored();
      await load();
    });

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Published to ${groupName}`}
      style={
        inline
          ? INLINE
          : { ...MENU, top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }
      }
    >
      {onPublish && (
        <>
          <button type="button" role="menuitem" disabled={busy} onClick={() => void publish()} style={ITEM}>
            <span>Publish changes</span>
            {latest !== undefined && <span style={MUTED}>→ v{latest + 1}</span>}
          </button>
        </>
      )}
      {message && <div style={NOTE}>{message}</div>}
      {error && <div style={{ ...NOTE, color: "var(--qf-negative)" }}>{error}</div>}

      {onPublish && <div style={RULE} />}
      <div style={HEADING}>History</div>
      {history === null ? (
        <div style={NOTE}>Loading…</div>
      ) : history.length === 0 ? (
        <div style={NOTE}>No versions yet — the next publish is v1.</div>
      ) : (
        history.map((entry) => (
          <div key={entry.version} style={ROW}>
            <span style={VERSION}>v{entry.version}</span>
            <span style={MUTED}>{when(entry.publishedAt)}</span>
            <span style={{ ...MUTED, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {entry.publishedBy}
            </span>
            {entry.version !== latest && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void restore(entry.version)}
                style={LINK}
                aria-label={`Restore v${entry.version}`}
              >
                Restore
              </button>
            )}
          </div>
        ))
      )}

      {onWithdraw && (
        <>
          <div style={RULE} />
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => void run(async () => {
              await onWithdraw();
              onClose();
            })}
            style={ITEM}
          >
            Withdraw from {groupName}
          </button>
        </>
      )}
    </div>
  );
}

/** `Sep 29 21:40`, in the viewer's own clock. */
function when(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Kept this far inside the window on every side. */
const EDGE = 4;

const MENU: React.CSSProperties = {
  position: "fixed",
  // Above the layout panel it opens from (1000).
  zIndex: 1001,
  minWidth: 280,
  padding: "4px 0",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
  fontSize: 12,
  color: "var(--qf-text)",
};
const INLINE: React.CSSProperties = {
  padding: "4px 0",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  fontSize: 12,
  color: "var(--qf-text)",
};
const ITEM: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  width: "100%",
  padding: "6px 12px",
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
};
const RULE: React.CSSProperties = { height: 1, margin: "4px 0", background: "var(--qf-border)" };
const HEADING: React.CSSProperties = {
  padding: "2px 12px",
  color: "var(--qf-text-muted)",
  fontSize: 11,
  fontWeight: 600,
};
const ROW: React.CSSProperties = { display: "flex", alignItems: "baseline", gap: 8, padding: "3px 12px 3px 20px" };
const VERSION: React.CSSProperties = { fontVariantNumeric: "tabular-nums", minWidth: 24 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", whiteSpace: "nowrap" };
const NOTE: React.CSSProperties = { padding: "2px 12px", color: "var(--qf-text-muted)", fontSize: 11 };
const LINK: React.CSSProperties = {
  padding: 0,
  border: 0,
  background: "none",
  color: "var(--qf-accent)",
  font: "inherit",
  cursor: "pointer",
};
